import { UserOutlined } from '@ant-design/icons'
import { PageHeader } from '@ant-design/pro-layout'
import { useQueryClient } from '@tanstack/react-query'
import { Descriptions, Input, Row, Select, Space, Tabs, Tag, Typography } from 'antd'
import { Avatar } from 'antd'
import day from 'dayjs'
import React, { useState } from 'react'
import Measure from 'react-measure'
import { useHistory } from 'react-router'
import styled from 'styled-components'
import useLocalStorageState from 'use-local-storage-state'

import { NewClientButton } from './NewClientButton'

import { getClients } from 'api/clients'
import { isClientFiltered } from 'constants/filteredClients'
import { ASSIGNMENT } from 'constants/numbers'
import { QUERIES } from 'constants/queries'
import { ROUTES } from 'constants/routes'
import { DEFAULT_PAGE_SIZE } from 'constants/table'
import { DataProvider } from 'shared/components/DataProvider'
import { Table } from 'shared/components/v2Table'
import { SidebarLayout } from 'shared/layouts'
import { formatPhone } from 'utils/formatters'
import { formatName } from 'utils/name'
import { route } from 'utils/router'

const StyledName = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  .ant-avatar {
    flex: none;
  }

  > span:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const COLUMNS = [
  {
    title: 'Name',
    dataIndex: 'firstName',
    key: 'firstName',
    fixed: 'left',
    width: 240,
    render: (text: any, client: any) => (
      <StyledName title={formatName(client)}>
        <Avatar alt={text} icon={<UserOutlined />} src={client?.profileImageSmall?.url} />
        <span>{formatName(client)}</span>
      </StyledName>
    ),
  },
  {
    title: 'Phone',
    dataIndex: 'masterPhoneNumber',
    key: 'masterPhoneNumber',
    width: 170,
    render: (phone: string) => (phone ? formatPhone(phone) : <Tag color="warning">Not Set</Tag>),
  },
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
    width: 270,
    ellipsis: true,
    render: (text: any) =>
      text && (
        <a href={`mailto:${text}`} title={text}>
          {text}
        </a>
      ),
  },
  {
    title: 'Account Created',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 155,
    render: (text: any) => text && day(text).format('MM/DD/YYYY'),
  },
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    width: 345,
    render: (id: any) => {
      return (
        <Typography.Text code copyable onClick={(e: any) => e.stopPropagation()}>
          {id}
        </Typography.Text>
      )
    },
  },
]

const TABLE_WIDTH = COLUMNS.reduce((total, column) => total + column.width, 0)

const paginationOptions = {
  position: ['none', 'bottomCenter'],
  pageSizeOptions: [50, 100],
  hideOnSinglePage: false,
  showSizeChanger: true,
}

const StyledContainer = styled.div`
  display: flex;
  flex-grow: 1;
  overflow: hidden;

  .ant-table-row {
    cursor: pointer;
  }

  .ant-table-wrapper {
    flex: 1;
    min-width: 0;
  }

  .ant-table-cell {
    white-space: nowrap;
  }
`

const StyledNav = styled.div`
  margin: 0 -10px;

  .ant-tabs-nav {
    margin: 0;
  }
`

const StyledStatistics = styled.div`
  margin-top: -32px;
`

export function ClientList() {
  const queryClient = useQueryClient()

  const history = useHistory()
  const [settings, setSettings] = useLocalStorageState('clients-list-settings', {
    assigned: ASSIGNMENT.ASSIGNED,
    billingStatus: 'active',
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    filters: null,
  })
  const [height, setHeight] = useState(0)
  const query = [QUERIES.CLIENTS, settings]

  function onTableSettingsChange(value: any) {
    setSettings({ ...value, assigned: settings.assigned })
  }

  function onSearch(value: any) {
    // @ts-expect-error TS(2345): Argument of type '{ search: any; page: number; pag... Remove this comment to see the full error message
    setSettings({ ...settings, search: value || '', page: 1, pageSize: DEFAULT_PAGE_SIZE })
  }

  function handleRowClick(record: any) {
    history.push(route(ROUTES.CLIENT.OVERVIEW, { id: record.id }))
  }

  function handleAssigned(value: any) {
    setSettings({ ...settings, assigned: value })
  }

  function handleBillingStatus(value: string) {
    setSettings({ ...settings, billingStatus: value, page: 1 })
  }

  return (
    <SidebarLayout>
      <DataProvider name={query} query={getClients(settings)}>
        {({ isLoading, data }: any) => {
          let pagination: any
          let clients: any = []
          if (data) {
            const [
              items,
              {
                paginationData: { pageNumber, pageSize, totalEntries },
              },
            ] = data
            clients = items.filter((c: any) => !isClientFiltered(c.id))
            pagination = {
              ...paginationOptions,
              total: totalEntries,
              current: pageNumber,
              pageSize,
            }
          }

          return (
            <>
              <PageHeader
                extra={
                  <Space align="start">
                    <NewClientButton onCreate={() => queryClient.invalidateQueries(query)} />
                    <Select
                      onChange={handleBillingStatus}
                      size="large"
                      style={{ width: 125 }}
                      value={settings.billingStatus ?? 'active'}
                    >
                      <Select.Option value="active">Active</Select.Option>
                      <Select.Option value="inactive">Inactive</Select.Option>
                      <Select.Option value="all">All</Select.Option>
                    </Select>
                    <div>
                      <Input.Search
                        allowClear
                        onChange={({ target: { value } }) => onSearch(value)}
                        onSearch={onSearch}
                        placeholder="Search"
                        size="large"
                        style={{ width: 300 }}
                        value={(settings as any).search}
                      />
                    </div>
                  </Space>
                }
                title="Clients"
              >
                <StyledStatistics>
                  <Row>
                    <Descriptions column={3} size="small">
                      <Descriptions.Item label="Total">{pagination?.total || '-'}</Descriptions.Item>
                    </Descriptions>
                  </Row>
                </StyledStatistics>
                <StyledNav>
                  <Tabs activeKey={settings.assigned} onChange={handleAssigned}>
                    <Tabs.TabPane key={ASSIGNMENT.ASSIGNED} tab="Assigned Numbers" />
                    <Tabs.TabPane key={ASSIGNMENT.UNASSIGNED} tab="Unassigned Numbers" />
                  </Tabs>
                </StyledNav>
              </PageHeader>
              <Measure
                bounds
                onResize={(contentRect: any) => {
                  setHeight(contentRect.bounds.height)
                }}
              >
                {({ measureRef }: any) => (
                  <StyledContainer ref={measureRef}>
                    <Table
                      columns={COLUMNS}
                      dataSource={clients}
                      loading={isLoading}
                      onRow={(record: any) => ({
                        onClick: () => handleRowClick(record),
                      })}
                      onSettingsChange={onTableSettingsChange}
                      pagination={pagination}
                      rowKey="id"
                      scroll={{ x: TABLE_WIDTH, y: `${height - 140}px` }}
                      settings={settings}
                    />
                  </StyledContainer>
                )}
              </Measure>
            </>
          )
        }}
      </DataProvider>
    </SidebarLayout>
  )
}
